import { tblSpecAddColumn } from '../../../../core/table/spec';
import { TblCol } from '../../../../core/table/table.types';
import { TblViewSpec } from '../../../../core/table/table-view.types';
import FormattedDateWithAMPMTimeRenderer from '../../../../core/table/renderers/FormattedDateWithAMPMTimeRenderer';
import CurrencyRenderer from '../../../../core/table/renderers/CurrencyRenderer';

/**
 * CpOrderInvoicesListTableSpec Column Reference
 *
 * | Category      | Column Name     | Type     |
 * |-------------- | --------------  | -------- |
 * | Order Info    | Order #         | string   |
 * | Order Info    | Type            | string   |
 * | Pharmacy      | Pharmacy        | string   |
 * | Vendor Info   | Name            | string   |
 * | Vendor Info   | Invoice #       | string   |
 * | Vendor Info   | Amount          | number   |
 * | Process Dates | Order Placed    | date     |
 * | Process Dates | Invoiced        | date     |
 * | Process Dates | Reconciled      | date     |
 *
 * Default sort column: Order Placed
 * Default sort order: Descending
 *
 * Notes:
 * - Column types are inferred from typical invoice table usage.
 * - Category headers are based on the UI screenshot provided.
 * - Adjust types as needed for your implementation.
 */

export type CpOrderColKey =
    | ''
    | 'OrderNum'
    | 'Type'
    | 'CEID'
    | 'Pharmacy'
    | 'VendorName'
    | 'VendorInvoiceNum'
    | 'TotalAmount'
    | 'DateCreated'
    | 'VendorInvoiceDate'
    | 'InvoiceReconciledDate';

// Column spec
export function cpOrderInvoicesListColumnSpec(): Map<CpOrderColKey, TblCol> {
    const map = new Map<CpOrderColKey, TblCol>();

    // Order Info
    tblSpecAddColumn<CpOrderColKey>(map, 'OrderNum', {
        type: 'TEXT',
        width: '100px',
        title: 'Order #',
    });
    tblSpecAddColumn<CpOrderColKey>(map, 'Type', {
        type: 'TEXT',
        width: '80px',
        title: 'Type',
    });

    // Facility (CE) — shown in Program Admin view
    tblSpecAddColumn<CpOrderColKey>(map, 'CEID', {
        type: 'TEXT',
        width: '120px',
        title: 'CEID',
    });

    // Pharmacy
    tblSpecAddColumn<CpOrderColKey>(map, 'Pharmacy', {
        type: 'TEXT',
        width: '120px',
        title: 'Pharmacy',
    });

    // Vendor Info
    tblSpecAddColumn<CpOrderColKey>(map, 'VendorName', {
        type: 'TEXT',
        width: '120px',
        title: 'Name',
    });
    tblSpecAddColumn<CpOrderColKey>(map, 'VendorInvoiceNum', {
        type: 'TEXT',
        width: '120px',
        title: 'Invoice #',
    });
    tblSpecAddColumn<CpOrderColKey>(
        map,
        'TotalAmount',
        {
            type: 'NUMBER',
            width: '100px',
            title: 'Amount',
        },
        CurrencyRenderer,
    );

    // Process Dates
    tblSpecAddColumn<CpOrderColKey>(
        map,
        'DateCreated',
        {
            type: 'DATE',
            width: '120px',
            title: 'Order Placed',
        },
        FormattedDateWithAMPMTimeRenderer,
    );
    tblSpecAddColumn<CpOrderColKey>(
        map,
        'VendorInvoiceDate',
        {
            type: 'DATE',
            width: '120px',
            title: 'Invoiced',
        },
        FormattedDateWithAMPMTimeRenderer,
    );
    tblSpecAddColumn<CpOrderColKey>(
        map,
        'InvoiceReconciledDate',
        {
            type: 'DATE',
            width: '120px',
            title: 'Reconciled',
        },
        FormattedDateWithAMPMTimeRenderer,
    );

    return map;
}

// Default view spec
export function cpOrderInvoicesListAllColumnsView(): TblViewSpec {
    return {
        key: 'all',
        title: 'All Columns',
        sections: [
            {
                title: 'Order Info',
                cols: ['OrderNum', 'Type'],
            },
            {
                title: 'Pharmacy',
                cols: ['Pharmacy'],
            },
            {
                title: 'Vendor Info',
                cols: ['VendorName', 'VendorInvoiceNum', 'TotalAmount'],
            },
            {
                title: 'Process Dates',
                cols: ['DateCreated', 'VendorInvoiceDate', 'InvoiceReconciledDate'],
            },
        ],
        sortby: '-VendorInvoiceDate',
    };
}
